import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { MessageCircle, Search } from "lucide-react";
import { lmsGetStudentChatThreads, lmsMarkChatThreadRead } from "../api/lmsApi";
import ChatThreadPanel from "../components/ChatThreadPanel";
import "../lms.css";

const initials = (name = "") =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";

const formatListTime = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString([], { day: "numeric", month: "short" });
};

const getStudentData = () => {
  try {
    const ud = JSON.parse(localStorage.getItem("userData") || "{}");
    const schoolId = ud.school_id || ud.admin_id || ud.id || null;
    const studentId = ud.student_id || ud.id || null;
    return { schoolId, studentId };
  } catch {
    return { schoolId: null, studentId: null };
  }
};

// Student's Chat inbox — one contact per teacher/admin who's messaged them,
// same two-pane layout as the staff side. Students reply into threads a
// staff member has already started; there's no "new chat" picker here.
export default function StudentChat() {
  const { schoolId, studentId } = getStudentData();

  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeStaff, setActiveStaff] = useState(null);

  const fetchThreads = async () => {
    try {
      const res = await lmsGetStudentChatThreads(studentId);
      setThreads(res.data?.data || []);
    } catch (err) {
      console.error("getStudentChatThreads error:", err);
      toast.error("Failed to load chats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (studentId) fetchThreads();
  }, [studentId]);

  const openThread = (t) => {
    setActiveStaff({ staff_type: t.staff_type, staff_id: t.staff_id, staff_name: t.staff_name });
    if (t.unread_count > 0) {
      lmsMarkChatThreadRead({
        student_id: studentId,
        staff_type: t.staff_type,
        staff_id: t.staff_id,
        reader_type: "student",
      })
        .then(() =>
          setThreads((prev) =>
            prev.map((x) =>
              x.staff_type === t.staff_type && x.staff_id === t.staff_id ? { ...x, unread_count: 0 } : x
            )
          )
        )
        .catch((err) => console.error("markChatThreadRead error:", err));
    }
  };

  const filteredThreads = threads.filter((t) =>
    (t.staff_name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div style={{ marginBottom: "12px" }}>
        <h2 style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "16px", fontWeight: 700, margin: 0 }}>
          <MessageCircle className="text-blue-600" size={18} />
          Chat
        </h2>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#64748b" }}>Loading chats...</div>
      ) : (
        <div
          style={{
            display: "flex",
            height: "65vh",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            overflow: "hidden",
            background: "#fff",
          }}
        >
          {/* Contact list */}
          <div
            style={{
              width: "240px",
              flexShrink: 0,
              borderRight: "1px solid #e2e8f0",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ padding: "12px", borderBottom: "1px solid #e2e8f0", flexShrink: 0 }}>
              <div style={{ position: "relative" }}>
                <Search
                  size={14}
                  style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }}
                />
                <input
                  type="text"
                  placeholder="Search chats..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 10px 8px 30px",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    fontSize: "13px",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </div>

            <div style={{ flex: 1, overflowY: "auto" }}>
              {filteredThreads.length === 0 ? (
                <div style={{ padding: "20px", textAlign: "center", color: "#94a3b8", fontSize: "13px" }}>
                  No chats yet.
                </div>
              ) : (
                filteredThreads.map((t) => {
                  const isActive =
                    activeStaff?.staff_type === t.staff_type && activeStaff?.staff_id === t.staff_id;
                  return (
                    <div
                      key={`${t.staff_type}_${t.staff_id}`}
                      onClick={() => openThread(t)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "10px 12px",
                        cursor: "pointer",
                        background: isActive ? "#eff6ff" : "transparent",
                        borderBottom: "1px solid #f1f5f9",
                      }}
                    >
                      <div
                        style={{
                          width: "38px",
                          height: "38px",
                          borderRadius: "50%",
                          background: t.staff_type === "admin" ? "#d97706" : "#2563eb",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "13px",
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {initials(t.staff_name)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span
                            style={{
                              fontWeight: 600,
                              fontSize: "13px",
                              color: "#0f172a",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {t.staff_name} {t.staff_type === "admin" ? "(Admin)" : ""}
                          </span>
                          <span style={{ fontSize: "11px", color: "#94a3b8", flexShrink: 0, marginLeft: "6px" }}>
                            {formatListTime(t.last_message_at)}
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#64748b",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {t.last_message || "No messages yet"}
                        </div>
                      </div>
                      {t.unread_count > 0 && (
                        <span
                          style={{
                            minWidth: "18px",
                            height: "18px",
                            borderRadius: "999px",
                            background: "#2563eb",
                            color: "#fff",
                            fontSize: "11px",
                            fontWeight: 700,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "0 5px",
                            flexShrink: 0,
                          }}
                        >
                          {t.unread_count}
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Active chat */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
            {!activeStaff ? (
              <div
                style={{
                  margin: "auto",
                  color: "#94a3b8",
                  fontSize: "13px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <MessageCircle size={32} color="#cbd5e1" />
                Select a chat to view the conversation.
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "12px 16px",
                    borderBottom: "1px solid #e2e8f0",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: "34px",
                      height: "34px",
                      borderRadius: "50%",
                      background: activeStaff.staff_type === "admin" ? "#d97706" : "#2563eb",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      fontWeight: 700,
                    }}
                  >
                    {initials(activeStaff.staff_name)}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "14px" }}>{activeStaff.staff_name}</div>
                    <div style={{ fontSize: "11px", color: "#94a3b8", textTransform: "capitalize" }}>
                      {activeStaff.staff_type}
                    </div>
                  </div>
                </div>

                <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
                  <ChatThreadPanel
                    key={`${activeStaff.staff_type}_${activeStaff.staff_id}`}
                    studentId={studentId}
                    staffType={activeStaff.staff_type}
                    staffId={activeStaff.staff_id}
                    viewerType="student"
                    viewerId={studentId}
                    schoolId={schoolId}
                    fill
                    onMessageSent={fetchThreads}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
